'use client';

import { Suspense, useState, useEffect, useCallback } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import useSWR from 'swr';
import { Layout } from '@/components/layout';
import { FilterPanel } from '@/components/search/FilterPanel';
import { SearchBar } from '@/components/search/SearchBar';
import { PatternGrid } from '@/components/patterns/PatternGrid';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react';
import type { Pattern } from '@/lib/supabase';
import { resilientFetch, getFriendlyErrorMessage } from '@/lib/resilient-fetch';

interface FilterState {
  categories: string[];
  languages: string[];
  tiers: string[];
  frameworks: string[];
}

interface SearchResponse {
  patterns: Pattern[];
  total: number;
  page: number;
  totalPages: number;
  hasMore: boolean;
}

interface PatternsResponse {
  patterns: Pattern[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

const fetcher = async (url: string) => {
  try {
    const res = await resilientFetch(url, {
      timeout: 10000, // 10 second timeout
      retries: 3,
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch patterns: HTTP ${res.status}`);
    }
    return res.json();
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(getFriendlyErrorMessage(error));
    }
    throw error;
  }
};

const parseUrlFilters = (param: string | null) => param?.split(',').filter(Boolean) || [];

function BrowseContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const query = searchParams.get('q') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);

  const [filters, setFilters] = useState<FilterState>({
    categories: parseUrlFilters(searchParams.get('categories')),
    languages: parseUrlFilters(searchParams.get('languages')),
    tiers: parseUrlFilters(searchParams.get('tiers')),
    frameworks: parseUrlFilters(searchParams.get('frameworks')),
  });
  const [searchQuery, setSearchQuery] = useState(query);

  const buildApiUrl = useCallback(() => {
    const p = new URLSearchParams();

    // If there's a search query, use /api/search, otherwise use /api/patterns
    if (searchQuery) {
      p.set('q', searchQuery);
      p.set('page', page.toString());
      if (filters.categories.length) p.set('category', filters.categories[0]);
      if (filters.languages.length) p.set('language', filters.languages[0]);
      if (filters.tiers.length) p.set('tier', filters.tiers[0]);
      if (filters.frameworks.length) p.set('framework', filters.frameworks[0]);
      return `/api/search?${p.toString()}`;
    } else {
      // Use /api/patterns for browsing all patterns
      p.set('page', page.toString());
      p.set('limit', '20');
      if (filters.categories.length) p.set('category', filters.categories[0]);
      if (filters.languages.length) p.set('language', filters.languages[0]);
      if (filters.tiers.length) p.set('tier', filters.tiers[0]);
      if (filters.frameworks.length) p.set('framework', filters.frameworks[0]);
      return `/api/patterns?${p.toString()}`;
    }
  }, [searchQuery, page, filters]);

  const { data: rawData, error, isLoading } = useSWR<SearchResponse | PatternsResponse>(buildApiUrl(), fetcher, { revalidateOnFocus: false });

  // Normalize data structure from both endpoints
  const data = rawData ? {
    patterns: rawData.patterns,
    total: 'pagination' in rawData ? rawData.pagination.total : rawData.total,
    page: 'pagination' in rawData ? rawData.pagination.page : rawData.page,
    totalPages: 'pagination' in rawData ? rawData.pagination.totalPages : rawData.totalPages,
    hasMore: 'pagination' in rawData ? rawData.pagination.hasNext : rawData.hasMore,
  } : undefined;

  const updateUrl = useCallback((newFilters: FilterState, newQuery: string, newPage: number) => {
    const params = new URLSearchParams();
    if (newQuery) params.set('q', newQuery);
    if (newPage > 1) params.set('page', newPage.toString());
    if (newFilters.categories.length) params.set('categories', newFilters.categories.join(','));
    if (newFilters.languages.length) params.set('languages', newFilters.languages.join(','));
    if (newFilters.tiers.length) params.set('tiers', newFilters.tiers.join(','));
    if (newFilters.frameworks.length) params.set('frameworks', newFilters.frameworks.join(','));
    router.push(params.toString() ? `/browse?${params.toString()}` : '/browse', { scroll: false });
  }, [router]);

  const handleFilterChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
    updateUrl(newFilters, searchQuery, 1);
  }, [searchQuery, updateUrl]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    updateUrl(filters, query, 1);
  }, [filters, updateUrl]);

  const handleClearFilters = useCallback(() => {
    const empty: FilterState = { categories: [], languages: [], tiers: [], frameworks: [] };
    setFilters(empty);
    updateUrl(empty, searchQuery, 1);
  }, [searchQuery, updateUrl]);

  const handlePageChange = useCallback((newPage: number) => {
    updateUrl(filters, searchQuery, newPage);
  }, [filters, searchQuery, updateUrl]);

  useEffect(() => {
    setSearchQuery(query);
    setFilters({
      categories: parseUrlFilters(searchParams.get('categories')),
      languages: parseUrlFilters(searchParams.get('languages')),
      tiers: parseUrlFilters(searchParams.get('tiers')),
      frameworks: parseUrlFilters(searchParams.get('frameworks')),
    });
  }, [query, searchParams]);

  const activeFilterCount = filters.categories.length + filters.languages.length +
    filters.tiers.length + filters.frameworks.length;

  return (
    <Layout>
      <div className="min-h-screen bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-100 mb-4">Browse Patterns</h1>
            <p className="text-gray-400">Discover and explore code patterns from the community</p>
          </div>

          <div className="mb-6">
            <SearchBar placeholder="Search patterns..." onSearch={handleSearch} showInstantResults={false} />
          </div>

          <div className="md:hidden mb-6">
            <Button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              iconLeft={<SlidersHorizontal className="h-4 w-4" />}
              className="w-full"
            >
              {isFilterOpen ? 'Hide' : 'Show'} Filters{activeFilterCount > 0 && ` (${activeFilterCount})`}
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <aside className="lg:col-span-1">
              <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onClear={handleClearFilters}
                isOpen={isFilterOpen}
                onToggle={() => setIsFilterOpen(!isFilterOpen)}
              />
            </aside>

            <div className="lg:col-span-3">
              {data && (
                <div className="mb-6 text-gray-400">
                  {data.total} pattern{data.total !== 1 ? 's' : ''} found
                  {activeFilterCount > 0 && ` (${activeFilterCount} filter${activeFilterCount !== 1 ? 's' : ''} active)`}
                </div>
              )}

              {error && (
                <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-6 text-center">
                  <p className="text-red-400 mb-2">Failed to load patterns</p>
                  <p className="text-red-500/70 text-sm">Please try again later</p>
                </div>
              )}

              <PatternGrid patterns={data?.patterns} isLoading={isLoading} />


              {data && data.totalPages > 1 && (
                <div className="mt-8 flex items-center justify-between">
                  <Button variant="secondary" onClick={() => handlePageChange(page - 1)} disabled={page <= 1} iconLeft={<ChevronLeft className="h-4 w-4" />}>
                    Previous
                  </Button>
                  <div className="flex items-center gap-2">
                    {Array.from({ length: Math.min(5, data.totalPages) }, (_, i) => (
                      <Button key={i + 1} variant={page === i + 1 ? 'primary' : 'secondary'} size="sm" onClick={() => handlePageChange(i + 1)}>
                        {i + 1}
                      </Button>
                    ))}
                    {data.totalPages > 5 && (
                      <>
                        <span className="text-gray-500">...</span>
                        <Button variant={page === data.totalPages ? 'primary' : 'secondary'} size="sm" onClick={() => handlePageChange(data.totalPages)}>
                          {data.totalPages}
                        </Button>
                      </>
                    )}
                  </div>
                  <Button variant="secondary" onClick={() => handlePageChange(page + 1)} disabled={!data.hasMore} iconRight={<ChevronRight className="h-4 w-4" />}>
                    Next
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default function BrowsePage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-gray-900 flex items-center justify-center"><div className="text-white">Loading...</div></div>}>
      <BrowseContent />
    </Suspense>
  );
}
